import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";

export type ServiceTemplateScheduleDisplayStatus =
    | "active"
    | "paused"
    | "archived"
    | "deleted";

// Statuses selectable in the schedules page status filter (PRO-10825).
// "deleted" is excluded — it's a computed fallback state for schedules whose
// client left the org, not something a user filters for; the status still
// renders on individual rows in the Status column. Encoding
// the exclusion in the type keeps it compiler-checked at every consumer.
export type ServiceTemplateScheduleFilterableStatus = Exclude<
    ServiceTemplateScheduleDisplayStatus,
    "deleted"
>;

export const SERVICE_TEMPLATE_SCHEDULE_FILTERABLE_STATUSES: ServiceTemplateScheduleFilterableStatus[] =
    ["active", "paused", "archived"];

export const serviceTemplateScheduleDisplayStatusLabel: Record<
    ServiceTemplateScheduleDisplayStatus,
    string
> = {
    active: "Active",
    paused: "Paused",
    archived: "Archived",
    deleted: "Deleted",
};

interface GetServiceTemplateScheduleDisplayStatusParams {
    status: ServiceTemplateRecurrenceStatus;
    isTemplateArchived: boolean;
    isClientDeleted: boolean;
}

/**
 * Computes the schedule's user-facing status, layering two client-only
 * signals on top of the persisted `status` column (PRO-10571):
 * - "archived": inferred when the schedule's close template has been
 *   archived — a schedule is never itself persisted as archived, archiving
 *   only lives on the template (see CloseTemplateEntity.archivedAt).
 * - "deleted": inferred when the schedule's client (workspace) is no longer
 *   assigned to the organization, so its entity can't be resolved from the
 *   org-scoped workspace list (`entityInfoByUuid`). The schedule row itself
 *   survives detaching a client from an org — only whole-workspace deletion
 *   cascades — but from this org's point of view the schedule now targets a
 *   client it no longer serves.
 * Archived/deleted take priority over the raw active/paused status since
 * they represent the schedule's practical fate, not just its stored flag.
 */
export function getServiceTemplateScheduleDisplayStatus({
    status,
    isTemplateArchived,
    isClientDeleted,
}: GetServiceTemplateScheduleDisplayStatusParams): ServiceTemplateScheduleDisplayStatus {
    if (isClientDeleted) {
        return "deleted";
    }
    if (isTemplateArchived) {
        return "archived";
    }
    return status === ServiceTemplateRecurrenceStatus.PAUSED
        ? "paused"
        : "active";
}
